import {injectable, inject} from 'inversify';
import * as FDC3 from 'openfin-fdc3';
import {WindowOption} from 'openfin/_v2/api/window/windowOption';
import {Identity} from 'openfin/_v2/identity';
import * as deepEquals from 'fast-deep-equal';
import {EntityType} from 'openfin/_v2/api/frame/frame';
import {EntityInfo} from 'openfin/_v2/api/system/entity';
import {View, Frame} from 'openfin/_v2/main';
import {_Window} from 'openfin/_v2/api/window/window';

import {Inject} from '../common/Injectables';
import {ServiceStore} from '../store/ServiceStore';
import {Action} from '../store/Store';
import {RootState} from '../store/State';
import {LaunchWindows} from '../store/partials/Authentication';
import {SetChannel, ChannelData, SetChannelContext, JoinChannel, ToggleChannelManagerWindow, DisplayInfo, MemberInfo, UpdateEntity, getIdentityKey} from '../store/partials/ChannelManager';
import {WebWindowFactory, WebWindow} from '../model/WebWindow';
import {renderComponentInWindow} from '../view/utils/RenderApp';
import {ChannelManagerApp} from '../view/apps/ChannelManager/ChannelManagerApp';
import {LAUNCHBAR_IDENTITY} from '../constants';
import {WINDOW_PREFIX} from '../config/windows';
import {Manifest} from '../types/manifest';
import {isOmnibar} from '../utils/processHelpers';

import {AsyncInit} from './AsyncInit';

// Correct type
interface EntityInfoTyped extends EntityInfo {
    entityType: EntityType;
}

const isProduction = process.env.NODE_ENV === 'production';

const windowOptions: WindowOption = {
    name: `${WINDOW_PREFIX}ChannelManager`,
    alwaysOnTop: true,
    autoShow: false,
    opacity: 0,
    frame: false,
    contextMenu: !isProduction,
    defaultCentered: true,
    defaultHeight: 434,
    defaultWidth: 1005,
    minWidth: 640,
    minHeight: 400,
    maximizable: true,
    minimizable: true,
    resizable: true,
    saveWindowState: false,
    shadow: true,
    showTaskbarIcon: true,
    url: '/window.html',
    waitForPageLoad: false,
    resizeRegion: {
        size: 3
    }
};

@injectable()
export class ChannelManager extends AsyncInit {
    private static readonly POLL_TIME: number = 10000;

    private _webWindow!: WebWindow;

    public constructor(
        @inject(Inject.STORE) private readonly store: ServiceStore,
        @inject(Inject.WEB_WINDOW_FACTORY) private readonly webWindowFactory: WebWindowFactory
    ) {
        super();
    }

    protected async init() {
        await this.store.initialized;

        if (!isOmnibar()) {
            this.store.onAction.add(this.onAction, this);

            this.setupWindowListeners();
            this.pollDisplayInfoChange();
            this.setupChannels().catch((e) => {
                console.error('Error setting up FDC3', e);
            });
        }
    }

    private async onAction(action: Action<RootState>): Promise<void> {
        if (action instanceof LaunchWindows) {
            this._webWindow = await this.webWindowFactory.create(windowOptions, true);
            const target = this._webWindow.document.getElementById('main')!;
            renderComponentInWindow(ChannelManagerApp, this._webWindow, this.store, target);
        } else if (action instanceof JoinChannel) {
            await action.channel.join(action.identity);
        } else if (action instanceof ToggleChannelManagerWindow) {
            if (await this._webWindow.isShowing()) {
                this._webWindow.hide();
            } else {
                await this._webWindow.show();
                this._webWindow.setAsForeground();
            }
        }
    }

    private async setupChannels() {
        // Mount FDC3 on the window for testing
        Object.assign(window, {fdc3: FDC3});

        const channels: FDC3.Channel[] = await FDC3.getSystemChannels();
        channels.push(FDC3.defaultChannel);

        await Promise.all(channels.map(async (channel) => {
            const context = await channel.getCurrentContext();
            const filteredMembers: Identity[] = (await channel.getMembers())
                .filter((identity) => this.allowIdentityCheck(identity));

            const memberInfo: MemberInfo[] = await Promise.all(filteredMembers.map(async (identity) => {
                const {entityType} = await fin.System.getEntityInfo(identity.uuid, identity.name!) as EntityInfoTyped;
                const visible = entityType === 'window'
                    ? await fin.Window.wrapSync(identity).isShowing()
                    : true;
                return {
                    identity,
                    channel,
                    display: await this.getDisplayInfo(identity),
                    visible
                };
            }));

            for (const member of memberInfo) {
                this.store.dispatch(new UpdateEntity(member.identity, member));
            }

            const channelData: ChannelData = {channel, context};
            await this.store.dispatch(new SetChannel(channelData));

            channel.addContextListener((ctx) => {
                this.store.dispatch(new SetChannelContext(channel.id, ctx));
            });
        }));

        FDC3.addEventListener('channel-changed', async (event: FDC3.ChannelChangedEvent) => {
            const {channel, identity} = event;
            if (this.allowIdentityCheck(identity)) {
                const change: Pick<MemberInfo, 'channel'> = {channel};
                this.store.dispatch(new UpdateEntity(identity, change));
            }
        });
    }

    private setupWindowListeners() {
        fin.System.addListener('view-created', async (event) => {
            if (this.allowIdentityCheck(event)) {
                const display = await this.getDisplayInfo(event);
                this.store.dispatch(new UpdateEntity(event, {visible: true, display}));
            }
        });

        fin.System.addListener('window-created', async (event) => {
            if (this.allowIdentityCheck(event)) {
                const win = fin.Window.wrapSync(event);
                const visible = await win.isShowing();
                const display = await this.getDisplayInfo(event);
                this.store.dispatch(new UpdateEntity(event, {visible, display}));
            }
        });

        fin.System.addListener('window-shown', async (event) => {
            if (this.allowIdentityCheck(event)) {
                this.store.dispatch(new UpdateEntity(event, {visible: true}));
            }
        });

        fin.System.addListener('window-hidden', async (event) => {
            if (this.allowIdentityCheck(event)) {
                this.store.dispatch(new UpdateEntity(event, {visible: false}));
            }
        });
    }

    private allowIdentityCheck(identity: Identity): boolean {
        if (identity.uuid !== LAUNCHBAR_IDENTITY.uuid) {
            return true;
        }
        return false;
    }

    private async getDisplayInfo(identity: Identity): Promise<DisplayInfo> {
        const entityInfo = await fin.System.getEntityInfo(identity.uuid, identity.name!) as unknown as EntityInfoTyped;

        const application = fin.Application.wrapSync(identity);
        const manifest: Manifest = await application.getManifest().catch(() => ({}));
        const {entityType} = entityInfo;
        let entity: _Window | View | Frame;
        if (entityType === 'window') {
            entity = fin.Window.wrapSync(identity);
        } else if (entityType === 'view') {
            entity = fin.View.wrapSync(identity);
        } else if (entityType === 'iframe') {
            entity = fin.Frame.wrapSync(identity);
        } else {
            return {name: 'Unknown Application', application: 'Unknown Application', icon: ''};
        }

        const {title} = await entity!.getInfo().catch(() => ({title: undefined}));
        const {startup_app: startupApp, platform} = manifest || {}; // eslint-disable-line camelcase
        const applicationName = (startupApp && startupApp.name) || (platform && platform.uuid);
        const applicationIcon = (startupApp && startupApp.icon);

        const displayInfo: DisplayInfo = {
            name: title || identity.name || 'Unknown Application',
            application: applicationName || 'Unknown Application',
            icon: applicationIcon || ''
        };

        return displayInfo;
    }

    /**
     * Check for display info changes on entities periodically.
     */
    private async pollDisplayInfoChange(): Promise<void> {
        setInterval(async () => {
            const entities = Object.values(this.store.state.channelManager.entities);
            await Promise.all(entities.map(async ({identity}) => {
                const display = await this.getDisplayInfo(identity);
                if (!deepEquals(display, this.store.state.channelManager.entities[getIdentityKey(identity)].display)) {
                    console.log('Update:', display, this.store.state.channelManager.entities[getIdentityKey(identity)].display);
                    this.store.dispatch(new UpdateEntity(identity, {display}));
                }
            }));
        }, ChannelManager.POLL_TIME);
    }
}

